/**
 * Silences a known Vue 3 bug where navigating away from a page that has
 * components in a pending/suspended state throws:
 *   TypeError: Cannot read properties of null (reading 'type')
 *   at unmountComponent
 *
 * This is a Vue 3 internals issue — the vnode.type check in unmountComponent
 * is not guarded against null subTrees that can occur during navigation when
 * async components or Suspense boundaries haven't resolved yet.
 *
 * This plugin owns the single `vueApp.config.errorHandler` slot and forwards
 * non-suppressed errors to Sentry. order:10 ensures it runs after Sentry's
 * auto-init so this handler wins. Do NOT add a `vue:error` hook — it fires
 * after errorHandler and would cause double-capture.
 *
 * G-245: si el error de runtime es un fallo de carga de chunk, lo enrutamos a
 * `error.vue` (showError) para no dejar la pantalla en blanco, y devolvemos. NO lo
 * capturamos aquí: el hook `vue:error` de Sentry ya lo captura (evita duplicado).
 *
 * HEALDESK-3P: en móvil, una sesión expirada hace que los GET subsecuentes
 * devuelvan 401 con un PostgrestError — un OBJETO plano `{ code, details, hint,
 * message }`, NO una instancia de Error — que a veces llega crudo hasta acá (p.ej.
 * re-thrown sin traducir desde un store, patrón `this.error = translateSupabaseError(e); throw e`).
 * `Sentry.captureException` sobre ese objeto generaba un issue ilegible titulado
 * 'Wr' ("Object captured as exception with keys: code, details, hint, message").
 * Dos guards nuevos, ANTES de la captura genérica:
 *   1) Expiración de sesión (PGRST303 / mensaje "JWT expired" / SessionExpiredError
 *      de PR#329) → NO se captura (ruido esperado, igual que SKIP_SENTRY_CODES en
 *      utils/supabase-error.ts) y se enruta a login vía `redirectToLoginExpired`.
 *   2) Cualquier otro PostgrestError crudo → se traduce con `translateSupabaseError`,
 *      que YA hace su propia captura a Sentry con un Error legible (mensaje = el
 *      mensaje real de Postgres, `.name` = código) y salta los códigos de ruido —
 *      así evitamos capturar el objeto crudo sin duplicar la captura. Deliberately
 *      NOT re-capturing here with the raw `details`/`hint` fields — those may carry
 *      row data and G-UX-007 forbids sending them to Sentry.
 */
import * as Sentry from '@sentry/nuxt'
import { isChunkLoadError } from '~/utils/isChunkLoadError'
import { translateSupabaseError } from '~/utils/supabase-error'
import { SessionExpiredError, redirectToLoginExpired } from '~/utils/sessionExpired'

// Plain PostgrestError shape (supabase-js throws these as plain objects, NOT
// Error instances — see utils/supabase-error.ts extractRawMessage).
function isRawPostgrestError(err: unknown): err is { code: string; message: string } {
  return (
    typeof err === 'object' && err !== null && !(err instanceof Error) &&
    typeof (err as { code?: unknown }).code === 'string' &&
    typeof (err as { message?: unknown }).message === 'string'
  )
}

function isJwtExpiredMessage(err: unknown): boolean {
  const message = err instanceof Error ? err.message : (err as { message?: unknown } | null)?.message
  return typeof message === 'string' && /jwt expired/i.test(message)
}

export default defineNuxtPlugin({
  name: 'vue-error-handler',
  order: 10, // run after Sentry auto-init so we own the final errorHandler slot
  setup(nuxtApp) {
    nuxtApp.vueApp.config.errorHandler = (err, _instance, info) => {
      const msg = String(err)
      if (
        msg.includes("Cannot read properties of null (reading 'type')") ||
        msg.includes("Cannot read properties of null (reading 'key')") ||
        // Misma carrera en unmountComponent (instance null): Chrome "Cannot
        // destructure property 'bum' of 'X' as it is null", Safari "... 'bum' from null".
        msg.includes("Cannot destructure property 'bum'")
      ) {
        return // Suppress known Vue unmount race — intentionally NOT sent to Sentry
      }
      // Edge fn returned a deliberate { error: "..." } business validation response.
      // translateSupabaseError already skips Sentry; guard here in case the error
      // also propagates through Vue's async call chain.
      if ((err as any).isEdgeFnBusinessError) return
      if (isChunkLoadError(err)) {
        // No capturamos aquí: el hook `vue:error` de Sentry ya captura este error
        // de forma independiente (evita evento duplicado). Solo mostramos la pantalla.
        showError(createError({
          fatal: true,
          statusCode: 503,
          statusMessage: 'No se pudo cargar la página',
          message: err instanceof Error ? err.message : String(err),
        }))
        return
      }

      // HEALDESK-3P — sesión expirada: enruta a login, NUNCA a Sentry. Es un
      // evento de ciclo de vida esperado (el 401 ya quedó en breadcrumbs), no un bug.
      const code = (err as { code?: unknown } | null)?.code
      if (
        err instanceof SessionExpiredError ||
        (err as { sessionExpired?: unknown } | null)?.sessionExpired === true ||
        code === 'PGRST303' ||
        isJwtExpiredMessage(err)
      ) {
        redirectToLoginExpired()
        return
      }

      // HEALDESK-3P — PostgrestError crudo (objeto plano, no Error) que llegó sin
      // traducir. translateSupabaseError() lo loguea, captura un Error legible a
      // Sentry (o lo omite si es un código de ruido conocido) y devuelve el mensaje
      // en español — lo reusamos en vez de volcar el objeto crudo, que producía el
      // título ilegible 'Wr'.
      if (isRawPostgrestError(err)) {
        translateSupabaseError(err)
        return
      }

      Sentry.captureException(err, { extra: { info } })
      console.error('[app error]', err, info)
    }
  },
})
